import { Clock, ShieldX, ShieldCheck, LinkIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useFormatters } from '@/shared/lib/formatting'
import type { SessionState } from '../types'

interface SessionStateScreenProps {
  // 'submitted' is the just-submitted confirmation (WPS-164 3A), distinct from
  // 'finalised' which is a revisit of an already-submitted session.
  state: SessionState | 'submitted'
  expiresAt?: string
  finalisedAt?: string | null
  isForm?: boolean
}

const ICONS: Record<string, { icon: React.ElementType; iconClass: string }> = {
  expired: { icon: Clock, iconClass: 'text-[var(--ink-3)]' },
  revoked: { icon: ShieldX, iconClass: 'text-[var(--ink-3)]' },
  submitted: { icon: ShieldCheck, iconClass: 'text-[var(--green)]' },
  finalised: { icon: ShieldCheck, iconClass: 'text-[var(--green)]' },
  archived: { icon: ShieldCheck, iconClass: 'text-[var(--ink-3)]' },
  not_found: { icon: LinkIcon, iconClass: 'text-[var(--ink-3)]' },
}

// State key → translation key stem. Copy lives in the translation layer so
// tenants can override it (e.g. "Evidence" → "Documentation") per-tenant.
const COPY_KEYS: Record<string, { title: string; description: string }> = {
  expired: { title: 'upload.expiredTitle', description: 'upload.expiredDescription' },
  revoked: { title: 'upload.revokedTitle', description: 'upload.revokedDescription' },
  // WPS-164 3A: a first submission reads "Evidence Submitted", not the
  // "Evidence already submitted" that wrongly implied a prior submission.
  submitted: { title: 'upload.successTitle', description: 'upload.successDescription' },
  finalised: { title: 'upload.finalisedTitle', description: 'upload.finalisedDescription' },
  archived: { title: 'upload.archivedTitle', description: 'upload.archivedDescription' },
  not_found: { title: 'upload.notFoundTitle', description: 'upload.notFoundDescription' },
}

export function SessionStateScreen({
  state,
  expiresAt,
  finalisedAt,
  isForm = false,
}: SessionStateScreenProps) {
  const { t } = useTranslation()
  const { locale } = useFormatters()
  const iconConf = ICONS[state] || ICONS.not_found
  const copyKeys = COPY_KEYS[state] || COPY_KEYS.not_found
  const formCopy =
    isForm && (state === 'submitted' || state === 'finalised')
      ? {
          title: t(
            'upload.form.successTitle',
            'Your form has been submitted',
          ),
          description: t(
            'upload.form.successDescription',
            'Thank you. Your answers have been sent.',
          ),
        }
      : null
  const c = {
    ...iconConf,
    title: formCopy?.title ?? t(copyKeys.title),
    description: formCopy?.description ?? t(copyKeys.description),
  }
  const Icon = c.icon

  return (
    <div className="panel-arrive flex flex-col items-center pt-16 text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--bg-subtle)]">
        <Icon size={32} className={c.iconClass} />
      </div>

      <h1 className="clara-heading mb-3 text-[24px]">{c.title}</h1>

      <p className="clara-body max-w-xs text-[var(--ink-2)]">{c.description}</p>

      {/* A return visit says when it was submitted, so it never reads as a fresh submission. */}
      {state === 'finalised' && finalisedAt && (
        <p className="clara-small mt-4 text-[var(--ink-3)]">
          {t('upload.finalisedOn', {
            defaultValue: 'Submitted on {{date}}',
            date: new Date(finalisedAt).toLocaleDateString(locale, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
          })}
        </p>
      )}

      {state === 'expired' && expiresAt && (
        <p className="clara-small mt-4 text-[var(--ink-3)]">
          Expired {new Date(expiresAt).toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </p>
      )}
    </div>
  )
}
